<template>
    <div class="time-interval-container">
      <label>{{ $t('device_transmission_optional') }}</label>
      <div class="input-group">
        <input 
          type="text" 
          v-model.number="value" 
          @input="handleInput"
          placeholder="Enter Value"
          class="interval-input"
        >
        <div class="dropdown">
          <button 
            @click.stop="toggleDropdown" 
            class="dropdown-toggle_"
            type="button"
          >
            {{ selectedUnit }}
            <span class="chevron" :class="{ 'open': isOpen }">▼</span>
          </button>
          <div class="dropdown-menu_" v-show="isOpen">
            <button 
              v-for="unit in units" 
              :key="unit"
              @click="selectUnit(unit)"
              class="dropdown-item font-14"
              type="button"
            >
              {{ unit }}
            </button>
          </div>
        </div>
      </div>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </div>
  </template>
  
  <script>
  export default {
    name: 'TimeIntervalInput',
    props: {
      maxHours: {
        type: Number,
        default: 24 // Maximum allowed time in hours
      },
      defaultValue: {
        type: Number,
        default: null // Default numeric value
      },
      defaultUnit: {
        type: String,
        default: 'Mins' // Default unit
      }
    },
    data() {
      return {
        value: this.defaultValue !== null ? this.defaultValue.toString() : '', // The numeric input value
        selectedUnit: this.defaultUnit, // Default unit
        isOpen: false, // Dropdown state
        units: ['Secs', 'Mins', 'Hr'], // Available units
        errorMessage: '' // Error message state
      };
    },
    methods: {
      toggleDropdown(e) {
        e.stopPropagation(); // Prevent click event from bubbling to document
        this.isOpen = !this.isOpen;
      },
      selectUnit(unit) {
        this.selectedUnit = unit;
        this.isOpen = false;
        this.validateInput();
      },
      handleInput() {
        this.validateInput();
      },
      validateInput() {
        const numericValue = parseFloat(this.value) || 0;
        let totalSeconds = 0;
  
        // Convert the input to seconds for comparison
        if (this.selectedUnit === 'Secs') {
          totalSeconds = numericValue;
        } else if (this.selectedUnit === 'Mins') {
          totalSeconds = numericValue * 60;
        } else if (this.selectedUnit === 'Hr') {
          totalSeconds = numericValue * 3600;
        }
  
        // Check if total time exceeds the maximum allowed
        const maxSeconds = this.maxHours * 3600;
        if (totalSeconds > maxSeconds) {
          this.errorMessage = this.$t('total-time-exceed', { maxHours: this.maxHours });
          this.$emit('error', true);
        } else {
          this.errorMessage = '';
          this.$emit('error', false);
        }
  
        // Emit the updated value and unit to the parent
        this.$emit('update', {
          intervalValue: numericValue,
          intervalPeriod: this.getBackendUnit(this.selectedUnit)
        });
      },
      getBackendUnit(unit) {
        // Map frontend units to backend units
        const unitMap = {
          Secs: 'seconds',
          Mins: 'minutes',
          Hr: 'hours'
        };
        return unitMap[unit] || unit;
      },
      handleOutsideClick(e) {
        if (!this.$el.contains(e.target)) {
          this.isOpen = false;
        }
      },
      getFrontendUnit(backendUnit) {
        // Map backend units to frontend units
        const unitMap = {
          seconds: 'Secs',
          minutes: 'Mins',
          hours: 'Hr'
        };
        return unitMap[backendUnit] || backendUnit;
      }
    },
    mounted() {
      // Prefill the default value with the corresponding frontend unit
      if (this.defaultValue !== null) {
        this.value = this.defaultValue.toString();
        this.selectedUnit = this.getFrontendUnit(this.defaultUnit);
      }
      document.addEventListener('click', this.handleOutsideClick);
    },
    beforeDestroy() {
      document.removeEventListener('click', this.handleOutsideClick);
    }
  };
  </script>
  
  <style scoped>
  .time-interval-container {
    font-family: system-ui, -apple-system, sans-serif;
    width: 100%;
    max-width: 400px;
  }
  
  .interval-label {
    display: block;
    color: #374151;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  .input-group {
    position: relative;
    display: flex;
    background: #F3F4F6;
    border-radius: 0.5rem;
  }
  
  .interval-input {
    flex: 1;
  }
  
  .interval-input::placeholder {
    color: #9CA3AF;
  }
  
  .dropdown {
    position: relative;
    min-width: 100px;
  }
  
  .dropdown-toggle_ {
    width: 100%;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    color: #374151;
    font-size: 14px;
  }
  
  .chevron {
    font-size: 0.75rem;
    margin-left: 0.5rem;
    transition: transform 0.2s ease;
  }
  
  .chevron.open {
    transform: rotate(180deg);
  }
  
  .dropdown-menu_ {
    position: absolute;
    top: 100%;
    right: 0;
    width: 100%;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    margin-top: 0.25rem;
    z-index: 9999;
  }
  
  .dropdown-item {
    width: 100%;
    padding: 0.75rem 1rem;
    text-align: left;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #374151;
    font-size: 14px;
  }
  
  .dropdown-item:hover {
    background: #F3F4F6;
  }
  
  .dropdown-item:first-child {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }
  
  .dropdown-item:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
  
  .error-message {
    color: #EF4444; /* Red color for errors */
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }
  button:focus{
    outline: none;
  }
  </style>
  